import { useMovableContext } from "components/Movables/MovableProvider";
import { useMemo } from "react";
import { MovableType, Movable, getBoxId } from "sockapi/scope-specs/round-spec";
import { useGetRoundStateNonReactive } from "stores/RoundStore";
import Globals from "Globals";

export const ROUND_VIEW_WIDTH_PX = 6000;

export type Rect = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotate: number;
};

export const getPiecesCSSScale = (): number | null => {
  const roundElem = document.getElementById("round-pieces");
  if (roundElem !== null) return getElemCSSScale(roundElem);
  const puzzleElem = document.getElementById("puzzle-content-wrapper");
  if (puzzleElem !== null) return 1;
  return null;
};

export const getBaseRect = (): Rect | null => {
  const elem =
    document.getElementById("round-base") ??
    document.getElementById("puzzle-content-wrapper") ??
    null;
  const scale = getPiecesCSSScale();
  if (elem === null || scale === null) return null;
  const { x, y, height, width } = elem.getBoundingClientRect();
  return { x, y, height: height / scale, width: width / scale, rotate: 0 };
};

export const posRelTo = <T extends { x: number; y: number }>(
  item: T,
  base: { x: number; y: number }
): T => {
  return { ...item, x: item.x - base.x, y: item.y - base.y };
};

export const posRelFrom = <T extends { x: number; y: number }>(
  base: T,
  delta?: { x: number; y: number }
): T => {
  return { ...base, x: base.x + (delta?.x ?? 0), y: base.y + (delta?.y ?? 0) };
};

export const clampRect = (innerRect: Rect, outerRect: Rect) => {
  const xMin =
    innerRect.rotate % 180 !== 0
      ? outerRect.x + (innerRect.height - innerRect.width) / 2
      : outerRect.x;
  const xMax =
    innerRect.rotate % 180 !== 0
      ? outerRect.x +
        outerRect.width -
        innerRect.height / 2 -
        innerRect.width / 2
      : outerRect.x + outerRect.width - innerRect.width;
  const x = Math.max(xMin, Math.min(innerRect.x, xMax));

  const yMin =
    innerRect.rotate % 180 !== 0
      ? outerRect.y + (innerRect.width - innerRect.height) / 2
      : outerRect.y;
  const yMax =
    innerRect.rotate % 180 !== 0
      ? outerRect.y +
        outerRect.height -
        innerRect.width / 2 -
        innerRect.height / 2
      : outerRect.y + outerRect.height - innerRect.height;
  const y = Math.max(yMin, Math.min(innerRect.y, yMax));

  return { ...innerRect, x, y };
};

export const posToRatio = (
  gridCols: number,
  gridRows: number,
  { x: ox, y: oy }: { x: number; y: number },
  rect: Rect
) => {
  const col = ((ox - rect.x - window.scrollX) * gridCols) / rect.width;
  const row = ((oy - rect.y - window.scrollY) * gridRows) / rect.height;
  return { col, row };
};

export const ratioToPos = (
  gridCols: number,
  gridRows: number,
  { col, row }: { col: number; row: number },
  rect: Rect
) => {
  const x = (rect.width * col) / gridCols + rect.x + window.scrollX;
  const y = (rect.height * row) / gridRows + rect.y + window.scrollY;
  return { x, y };
};

const ratioToSize = (
  gridCols: number,
  gridRows: number,
  { cols, rows }: { cols: number; rows: number },
  base: Rect
) => {
  return {
    // (!) note that height and width are always relative to base.height
    width: (base.width * cols) / gridCols,
    height: (base.height * rows) / gridRows,
  };
};

export const snapRatio = ({ col, row }: { col: number; row: number }) => ({
  col: Math.round(col),
  row: Math.round(row),
});

export const useSnapPos = () => {
  const ctx = useMovableContext();

  return useMemo(() => {
    const snapPos = <T extends { x: number; y: number }>(
      { x, y, ...rest }: T,
      base: Rect
    ) => {
      const gridCols =
        ctx.type === "puzzle" ? ctx.puzzle.cols : ctx.round!.gridCols;
      const gridRows =
        ctx.type === "puzzle" ? ctx.puzzle.rows : ctx.round!.gridRows;
      return {
        ...ratioToPos(
          gridCols,
          gridRows,
          snapRatio(posToRatio(gridCols, gridRows, { x, y }, base)),
          base
        ),
        ...rest,
      };
    };

    return snapPos;
  }, [ctx]);
};

export const useMovableToRect = () => {
  const getRoundStateNonReactive = useGetRoundStateNonReactive();
  const ctx = useMovableContext();

  return useMemo(() => {
    const toRect = (
      m: Movable,
      base: Rect
    ): Rect & { scaleX: number; zIndex: number } => {
      if (getRoundStateNonReactive === null)
        throw new Error("toRect called before round state is initialized");
      // Make sure all pieces are rendered above all boxes.
      let zIndex = m.type === MovableType.BOX ? m.order : m.order + 100;
      if (m.type === MovableType.BOX && m.order > 100) {
        console.warn(
          "Box detected with order > 100; this would cause unintended z-ordering."
        );
        zIndex = 99;
      }
      // TODO: Check this again at the end to ensure that we don't
      // exceed the z-index of any element that could be present
      // at the same time as movables and we want to render over.
      // When this was last updated, the lowest such z-index is
      // reserved for puzzle layers that should go above pieces.
      if (zIndex >= Globals.zIndices.PUZZLE_LAYERS_ABOVE_PIECES_START) {
        console.warn(
          "Movable detected with z-index too high; this would cause unintended z-ordering."
        );
        zIndex = Globals.zIndices.PUZZLE_LAYERS_ABOVE_PIECES_START - 1;
      }
      if (ctx.type === "puzzle") {
        return {
          ...ratioToPos(ctx.puzzle.cols, ctx.puzzle.rows, m, base),
          width: base.width * (m.cols / ctx.puzzle.cols),
          height: base.height * (m.rows / ctx.puzzle.rows),
          rotate: m.rot,
          scaleX: m.flipped ? -1 : 1,
          zIndex,
        };
      }

      switch (m.type) {
        case MovableType.BOX:
        case MovableType.PIECE: {
          return {
            ...ratioToPos(ctx.round!.gridCols, ctx.round!.gridRows, m, base),
            ...ratioToSize(ctx.round!.gridCols, ctx.round!.gridRows, m, base),
            rotate: m.rot,
            scaleX: m.flipped ? -1 : 1,
            zIndex,
          };
        }
        case MovableType.BOX_PIECE: {
          const { derived } = getRoundStateNonReactive();
          const mBox = derived.movables[getBoxId(m)]!;
          const mRect = toRect(mBox, base);
          return {
            ...ratioToPos(mBox.cols, mBox.rows, m, mRect),
            ...ratioToSize(ctx.round!.gridCols, ctx.round!.gridRows, m, base),
            rotate: m.rot,
            scaleX: m.flipped ? -1 : 1,
            zIndex,
          };
        }
        // Missing cases would result in no value returned and
        // fail the typecheck against the return type.
      }
    };

    return toRect;
  }, [getRoundStateNonReactive, ctx]);
};

export const getElemCSSScale = (el: HTMLElement) => {
  return el.style.scale === "" ? 1 : Number(el.style.scale.trim());
};
